import { CreateStandaloneChargeActivationDTO } from '../../../services/standalone-charges';
import { unmaskCpfOrCnpj } from '../../../services/masks';

import { ActivationFormState } from '../types';
import {
  normalizeBankHolderDocument,
  normalizeComplementary,
  normalizePagarmeBankHolderName,
  normalizeRecipientDocument,
  normalizeReferencePoint,
  toBrazilianBirthdate,
  toPagarmePhoneNumbers
} from './activationFormHelpers';

export const buildPagarmeActivationPayload = (
  form: ActivationFormState
): CreateStandaloneChargeActivationDTO => {
  const recipientDocument = normalizeRecipientDocument(form.document, form.recipientType);
  const holderDocument = normalizeBankHolderDocument(
    form.bankHolderDocument,
    form.recipientType,
    form.bankHolderType
  );

  const commonRegisterInformation = {
    email: form.email.trim().toLowerCase(),
    document: recipientDocument,
    type: form.recipientType
  };

  return {
    recipientType: form.recipientType,
    registerInformation: {
      ...commonRegisterInformation,
      ...(form.recipientType === 'corporation'
        ? {
            company_name: form.companyName.trim(),
            trading_name: form.tradingName.trim(),
            annual_revenue: Number(form.annualRevenue),
            phone_numbers: toPagarmePhoneNumbers(form.phone),
            main_address: {
              street: form.street.trim(),
              complementary: normalizeComplementary(form.complementary),
              street_number: form.streetNumber.trim(),
              neighborhood: form.neighborhood.trim(),
              city: form.city.trim(),
              state: form.state.trim().toUpperCase(),
              zip_code: unmaskCpfOrCnpj(form.zipCode).replace(/\D/g, '').slice(0, 8),
              reference_point: normalizeReferencePoint(form.referencePoint)
            },
            managing_partners: [
              {
                name: form.partnerName.trim(),
                email: form.partnerEmail.trim().toLowerCase(),
                document: unmaskCpfOrCnpj(form.partnerDocument).replace(/\D/g, '').slice(0, 11),
                type: 'individual',
                mother_name: form.partnerMotherName.trim() || undefined,
                birthdate: toBrazilianBirthdate(form.partnerBirthdate),
                monthly_income: Number(form.partnerMonthlyIncome),
                professional_occupation: form.partnerProfessionalOccupation.trim(),
                self_declared_legal_representative: true,
                phone_numbers: toPagarmePhoneNumbers(form.partnerPhone),
                address: {
                  street: form.partnerStreet.trim() || form.street.trim(),
                  complementary: normalizeComplementary(
                    form.partnerComplementary.trim() || form.complementary.trim()
                  ),
                  street_number:
                    form.partnerStreetNumber.trim() || form.streetNumber.trim(),
                  neighborhood:
                    form.partnerNeighborhood.trim() || form.neighborhood.trim(),
                  city: form.partnerCity.trim() || form.city.trim(),
                  state: (form.partnerState.trim() || form.state.trim()).toUpperCase(),
                  zip_code:
                    unmaskCpfOrCnpj(form.partnerZipCode).replace(/\D/g, '').slice(0, 8) ||
                    unmaskCpfOrCnpj(form.zipCode).replace(/\D/g, '').slice(0, 8),
                  reference_point:
                    form.partnerReferencePoint.trim() ||
                    normalizeReferencePoint(form.referencePoint)
                }
              }
            ]
          }
        : {
            name: form.name.trim(),
            mother_name: form.motherName.trim() || undefined,
            birthdate: toBrazilianBirthdate(form.birthdate),
            monthly_income: Number(form.monthlyIncome),
            professional_occupation: form.professionalOccupation.trim(),
            phone_numbers: toPagarmePhoneNumbers(form.phone),
            address: {
              street: form.street.trim(),
              complementary: normalizeComplementary(form.complementary),
              street_number: form.streetNumber.trim(),
              neighborhood: form.neighborhood.trim(),
              city: form.city.trim(),
              state: form.state.trim().toUpperCase(),
              zip_code: unmaskCpfOrCnpj(form.zipCode).replace(/\D/g, '').slice(0, 8),
              reference_point: normalizeReferencePoint(form.referencePoint)
            }
          })
    },
    defaultBankAccount: {
      holder_name: normalizePagarmeBankHolderName(form.bankHolderName),
      holder_type: form.bankHolderType,
      holder_document: holderDocument,
      bank: form.bankCode.trim(),
      branch_number: form.bankBranchNumber.trim(),
      branch_check_digit: form.bankBranchCheckDigit.trim() || undefined,
      account_number: form.bankAccountNumber.trim(),
      account_check_digit: form.bankAccountCheckDigit.trim(),
      type: 'checking'
    }
  };
};
